import { logEvent } from "$lib/analyticsPosthog";
import { Attending, ContactPreference } from "$lib/eventTypes";
import { beginAtString } from "$lib/formSchemas";
import { routes, withHost } from "$lib/routes";
import { sendEmailsInBatch, sendEventCreatedEmail } from "$lib/server/email";
import { sendSms } from "$lib/server/sms";
import { addBroadcast, deleteOwners, getEventById, getUser, upsertVisitor } from "$lib/server/supabase";
import { getContext } from "telefunc";
import { closeDrizzleInstance, getDrizzleInstance } from '$lib/server/drizzle/instance';
import { sql, eq } from 'drizzle-orm'
import { events, eventQuestions } from '$lib/server/drizzle/schema';
import type { PgColumn } from "drizzle-orm/pg-core";
import { newOwnerSecret, slugify } from "$lib/commonLogic";
import { uuid } from "$lib/common";
import { addOwner } from "$lib/server/event";

export async function removeOwnerFromEvent(eventId: string, ownerId: string) {
    const { userId } = getContext();

    const event = await getEventById(eventId);
    if (!event) throw new Error("Event not found");
    if (!event.owners.some(x => x.userId === userId)) throw new Error("You are not the owner of this event");

    await deleteOwners(eventId, [ownerId]);
}

export async function sendBroadcast(eventId: string, text: string, pinMessage: boolean) {
    const { userId } = getContext();
    const event = await getEventById(eventId);
    if (!event) return { error: "Event not found" };
    if (!event.owners.some(x => x.userId === userId)) return { error: "You are not the owner of this event" };
    text = text.trim();
    if (!text) return { error: "Text is empty" };
    if (text.length > 320) return { error: "Text is too long. Must be less then 500 characters" };

    try {
        const guests = event.guests.filter(x => x.attending === Attending.ATTENDING || x.attending === Attending.MAYBE)
            .filter(x => x.userId !== userId)
        const emails = guests.filter(x => x.contactPreference === ContactPreference.EMAIL && x.email)
            .map(x => x.email!)
        const phones = guests.filter(x => x.contactPreference === ContactPreference.PHONE && x.phone)
            .map(x => x.phone?.replace(nonNumbers, '').replace(/^00/, '').replace(/^0/, '49')!)

        await Promise.all([
            sendSms(phones, `Info zu: ${event.name}\n` + text),
            sendEmailsInBatch(emails, `Info zu: ${event.name}`, `Info zu: <a href="${withHost(routes.event(event.slug))}">${event.name}</a><br><br>` + text.replace(/\n/g, '<br>').replace(/  /g, '&nbsp;&nbsp;')),
            addBroadcast({ eventId, author: userId, message: text, pinned: pinMessage, sentEmails: emails.length, sentSms: phones.length }),
            logEvent("message_broadcast", { userId, eventId, phones: phones.length, emails: emails.length })
        ])

    } catch (error) {
        console.error("Error sending broadcast", error)
        return { error: error }
    }
}

const nonNumbers = /[^\p{N}]/gu

export async function cloneEvent(eventId: string, beginDate: Date, beginTime: string, name: string) {
    const { userId, timezone } = getContext();

    try {
        const { id: clonedEventId } = await cloneEventAndReferencingRows(eventId, beginDate, beginTime, timezone, name)

        const [user, clonedEvent] = await Promise.all([
            getUser(userId),
            getDrizzleInstance().query.events.findFirst({ where: eq(events.id, clonedEventId) }),
            addOwner(clonedEventId, userId),
            upsertVisitor(clonedEventId, userId),
        ])
        if (!user) throw new Error("User not found")
        if (!clonedEvent) throw new Error("Cloned event not found")

        await sendEventCreatedEmail(clonedEvent, user.email!);

        logEvent('event_created', {
            eventId: clonedEvent.id,
            eventName: clonedEvent.name,
            eventCreated: clonedEvent.createdAt,
            userId: user.id,
        });

        return clonedEvent;
    } finally {
        closeDrizzleInstance()
    }


    // TODO create unique constraint auf secret
    // clone image otherweise if original event gets deleted image is gone?
}


async function cloneEventAndReferencingRows(eventId: string, beginDate: Date, beginTime: string, timezone: string, name: string) {
    const colName = (column: PgColumn) => sql.raw(column.name)

    const newId = uuid()
    const slug = slugify(name)
    const beginAt = beginAtString(beginDate, beginTime, timezone)
    const secret = newOwnerSecret()

    console.log("time from browser", { beginDate: beginDate, beginTime: beginTime, timezone });
    console.log("converted time", beginAtString(beginDate, beginTime, timezone));
    console.log("servers time " + new Date() + "" + Intl.DateTimeFormat().resolvedOptions().timeZone);

    const db = getDrizzleInstance()

    // doing all this jazz to clone a generic row and dont need to update this code when other columns are added or changed
    const query = sql`
        WITH ins_events AS (
            INSERT INTO ${events}
            SELECT *
            FROM jsonb_populate_record(null::${events}, (
              SELECT row_to_json(t)::jsonb ||
                (
                  '{
                        "${colName(events.id)}": "'||${newId}::text||'", 
                        "${colName(events.name)}": "'||${name}::text||'", 
                        "${colName(events.slug)}": "'||${slug}::text||'", 
                        "${colName(events.beginAt)}": "'||${beginAt}::text||'",
                        "${colName(events.ownerSecret)}": "'||${secret}::text||'", 
                        "${colName(events.createdAt)}": "'||now()::text||'", 
                        "${colName(events.updatedAt)}": "'||now()::text||'"
                    }'
                )::jsonb as p
              FROM ${events} t
              WHERE t.${colName(events.id)} = ${eventId}
            ))
            RETURNING ${colName(events.id)}
        )

        INSERT INTO ${eventQuestions}
        SELECT(jsonb_populate_record(null::${eventQuestions}, t.c)).*
        FROM(
            SELECT row_to_json(eq)::jsonb ||
            (
                '{
                    "${colName(events.id)}": "'||nextval(pg_get_serial_sequence('${eventQuestions}', '${colName(events.id)}'))::text||'",
                    "${colName(eventQuestions.eventId)}": "'||(SELECT id::text FROM ins_events)||'",
                    "${colName(eventQuestions.createdAt)}": "'||now()::text||'",
                    "${colName(eventQuestions.updatedAt)}": "'||now()::text||'"
                }'
            )::jsonb as c
            FROM ${eventQuestions} eq
            WHERE eq.${colName(eventQuestions.eventId)} = ${eventId}
        ) t
    `
    await db.execute(query)

    return { id: newId, secret }

    /** its like this
      WITH ins_events AS (
            INSERT INTO events
            SELECT *
            FROM jsonb_populate_record(null::events, (
              SELECT
                row_to_json(t)::jsonb ||
                (
                  '{
                        "id": "'||gen_random_uuid()::text||'", 
                        "slug": "'||gen_random_uuid()::text||'", 
                        "created_at": "'||now()::text||'", 
                        "updated_at": "'||now()::text||'"
                    }'
                )::jsonb as p
              FROM   events t
              WHERE  id = '7cca853f-e432-4234-a2db-3c4cf6fe7082'
            ))
            RETURNING id
          )
            INSERT INTO event_questions
            SELECT (jsonb_populate_record(null::event_questions, t.c)).*
            FROM (
              SELECT
                row_to_json(sub)::jsonb ||
                (
                  '{
                        "id": "'||nextval(pg_get_serial_sequence('event_questions', 'id'))::text||'",
                        "event_id":"'||(SELECT id::text FROM ins_events)||'"
                    }'
                )::jsonb as c
              FROM   event_questions sub
              WHERE  event_id = '7cca853f-e432-4234-a2db-3c4cf6fe7082'
            ) t
     */
}